import { Dispatch } from 'react';

import * as BlinkCardSDK from '@microblink/blinkcard-in-browser-sdk';
import {
  BlinkCardRecognizerResult,
  PreferredCameraType,
} from '@microblink/blinkcard-in-browser-sdk';
import { MetadataCallbacks } from '@microblink/blinkcard-in-browser-sdk/src/MicroblinkSDK/MetadataCallbacks';
import { WasmSDKWorker } from '@microblink/blinkcard-in-browser-sdk/types/MicroblinkSDK/worker/WorkerSDKBackend';

export async function startDebitCardScan(
  blinkCardWasmSdk: WasmSDKWorker,
  cameraFeed: HTMLVideoElement,
  onScanSuccess: (result: BlinkCardRecognizerResult) => void,
  setCancelScan: Dispatch<any>,
  recognizerRunnerCallbacks: MetadataCallbacks,
  onError: (errorMessage: string) => void
) {
  let videoRecognizer;

  if (!blinkCardWasmSdk) {
    return;
  }

  const blinkCardRecognizer = await BlinkCardSDK.createBlinkCardRecognizer(
    blinkCardWasmSdk
  );

  const settings = await blinkCardRecognizer?.currentSettings();
  if (
    typeof settings === 'object' &&
    settings !== null &&
    settings !== undefined
  ) {
    settings['extractCvv'] = false;
    await blinkCardRecognizer.updateSettings(settings);
  }

  const recognizerRunner = await BlinkCardSDK.createRecognizerRunner(
    blinkCardWasmSdk,
    [blinkCardRecognizer],
    true,
    {
      ...recognizerRunnerCallbacks,
      onFirstSideResult: () => {
        // Cancel scan, this will complete the recognition and call onSuccess
        // @link: https://github.com/blinkcard/blinkcard-in-browser/blob/29ecdf830cff61b31b0ed69489e2fa6a94cab76b/src/MicroblinkSDK/VideoRecognizer.ts#L414
        videoRecognizer?.cancelRecognition();
      },
    }
  );

  try {
    const selectedCamera = await BlinkCardSDK.selectCamera(
      null,
      PreferredCameraType.BackFacingCamera
    );

    videoRecognizer = await BlinkCardSDK.VideoRecognizer.createVideoRecognizerFromCameraStream(
      cameraFeed,
      recognizerRunner,
      selectedCamera?.deviceId || null
    );

    // Set the cancel function so that the videoRecognizer is within its closure
    setCancelScan({
      run: () => {
        videoRecognizer?.cancelRecognition();
      },
    });

    // The recognize() method will hold runtime until the camera
    // has captured a card, capture is cancelled or there is an error.
    const processResult = await videoRecognizer.recognize();

    // This runs after the videoRecognizer has captured a card
    if (processResult !== BlinkCardSDK.RecognizerResultState.Empty) {
      const recognitionResult = await blinkCardRecognizer.getResult();
      onScanSuccess(recognitionResult);
    }

    // Release browser resources associated with the camera stream
    videoRecognizer?.releaseVideoFeed();

    // // Release memory on WebAssembly heap used by the RecognizerRunner
    recognizerRunner?.delete();

    // // Release memory on WebAssembly heap used by the recognizer
    blinkCardRecognizer?.delete();
  } catch (error) {
    if (error.name === 'VideoRecognizerError') {
      // Reason is of type BlinkCardSDK.NotSupportedReason and contains information why video
      // recognizer could not be used. Usually this happens when user didn't grant access to a
      // camera or when a hardware or OS error occurs.
      const reason = (error as BlinkCardSDK.SDKError).details.reason;
      onError(reason);
    }

    if (error.name === 'NotAllowedError') {
      onError('Camera Access Denied');
    }
  }
}
